import { Component, EventEmitter, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import {
  concatMap,
  finalize,
  first,
  map,
  take,
  takeUntil,
} from 'rxjs/operators';
import { UploadService } from '../../../upload/upload.service';
import { BehaviorSubject, combineLatest, defer, from } from 'rxjs';
import {
  ImageMetadataService,
  isUnsupportedJpegColorSpaceError,
} from '../../../core/image/image-metadata.service';
import { FullscreenService } from '../../fullscreen/fullscreen.service';
import { OrganizationService } from '../../../core/organizations/organization.service';
import { UploadContext } from 'Upload/upload/graphql';
import { ImagesInContextDrawerComponent } from '../../image-management/images-in-context-drawer/images-in-context-drawer.component';
import { ZalamoDialogService } from '../../zalamo-dialog/zalamo-dialog.service';

@Component({
    selector: 'lib-photo-selection',
    templateUrl: './photo-selection.component.html',
    styleUrls: ['./photo-selection.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PhotoSelectionComponent implements OnInit {
  acceptedFormats = ['image/jpeg', 'image/png'];
  isPhonePortrait$ = this.screenObserver
    .observe(Breakpoints.Handset)
    .pipe(map(({ matches }) => matches));
  isUploading$ = new BehaviorSubject(false);
  isFromZalamoCoping$ = new BehaviorSubject(false);
  filePicked = new EventEmitter<{
    fileName: string;
    size: { w: number; h: number };
  }>();
  @Input() coverType: 'public' | 'private' | 'booking' = 'public';

  @Input() context: UploadContext;
  @Input() contextId: string;

  constructor(
    private screenObserver: BreakpointObserver,
    private uploadService: UploadService,
    private metadata: ImageMetadataService,
    private fullscreenService: FullscreenService,
    private dialog: ZalamoDialogService,
    private organizationService: OrganizationService,
  ) {}

  ngOnInit(): void {}

  uploadFromDisk(input: HTMLInputElement) {
    if (this.acceptedFormats.indexOf(input.files[0].type) === -1) {
      this.fullscreenService.open({
        content: 'Okładka obsługuje tylko pliki jpeg i png',
      });
      return;
    }
    this.isUploading$.next(true);
    combineLatest([
      this.uploadService.uploadSingleFile(
        input.files[0],
        this.coverType === 'public'
          ? UploadContext.CoverPublic
          : this.coverType === 'private'
          ? UploadContext.CoverPrivate
          : UploadContext.CoverBooking,
        this.contextId,
        this.organizationService.lastOrganizationId,
      ),
      from(this.metadata.getDetails(input.files[0])),
    ])
      .pipe(first())
      .subscribe({
        next: ([fileName, details]) => {
          console.log(fileName, details);
          this.isUploading$.next(false);

          this.filePicked.emit({
            fileName,
            size: {
              w: !!details.file
                ? details.file['Image Width'].value
                : details.pngFile['Image Width'].value,
              h: !!details.file
                ? details.file['Image Height'].value
                : details.pngFile['Image Height'].value,
            },
          });
        },
        error: (error) => {
          this.isUploading$.next(false);
          if (!isUnsupportedJpegColorSpaceError(error)) {
            console.error(error);
          }
        },
      });
  }

  uploadFromZalamo() {
    if (this.isFromZalamoCoping$.value) {
      return;
    }
    const ref = this.dialog.openFull(ImagesInContextDrawerComponent, {
      closable: true,
    });
    const { componentInstance } = ref;
    componentInstance.context = this.context as any;
    componentInstance.contextId = this.contextId;
    componentInstance.isPicker = true;
    componentInstance.pickerSingleMode = true;
    componentInstance.selected
      .pipe(
        take(1),
        takeUntil(ref.afterClosed()),
        // Stop selections on close, but keep copying even when close emits synchronously.
        concatMap(([{ photo }]) =>
          defer(() => {
            this.isFromZalamoCoping$.next(true);
            ref.close();
            return this.uploadService.moveToPublic(photo.fileName);
          }).pipe(
            map(() => ({
              fileName: `public/${photo.fileName}`,
              size: {
                w: photo.width,
                h: photo.height,
              },
            })),
            finalize(() => this.isFromZalamoCoping$.next(false)),
          ),
        ),
      )
      .subscribe({
        next: (cover) => this.filePicked.emit(cover),
        error: (error: unknown) => {
          console.error(error);
          this.fullscreenService.open({
            content: 'Nie udało się ustawić okładki. Spróbuj ponownie.',
          });
        },
      });
  }
}
